/** @format */

import React from "react";

/**
 * A flexible loading spinner component.
 *
 * @param {'sm' | 'md' | 'lg' | 'xl'} [size='md'] - Size of the spinner.
 * @param {'inline' | 'overlay' | 'full-page' | 'button'} [variant='inline'] - How the spinner should be displayed.
 *    'inline': Basic spinner and text, flows with content.
 *    'overlay': Covers its parent element (parent needs `position: relative`).
 *    'full-page': Covers the entire viewport (uses `position: fixed`).
 *    'button': Optimized for use inside a button (typically smaller, no text).
 * @param {string} [text] - Optional text displayed next to/below the spinner.
 * @param {string} [className=''] - Additional CSS classes for the container.
 * @param {string} [overlayColor='bg-white/70 backdrop-blur-sm'] - Background for 'overlay' and 'full-page' variants.
 */
export const LoadingSpinner = ({
	size = "md",
	variant = "inline",
	text,
	className = "",
	overlayColor = "bg-white/70 backdrop-blur-sm", // Default semi-transparent overlay
}) => {
	// Define size classes for the spinner itself
	const sizeClasses = {
		sm: "h-4 w-4 border-2", // Small, good for buttons
		md: "h-6 w-6 border-[3px]", // Medium (default)
		lg: "h-8 w-8 border-4", // Large
		xl: "h-12 w-12 border-4", // Extra Large (like original full-page)
	};

	// Spinner element JSX
	const spinnerElement = (
		<div
			className={`animate-spin rounded-full border-primary border-t-transparent ${
				sizeClasses[size] || sizeClasses["md"]
			}`}
			role="status" // Accessibility
			aria-live="polite"
			aria-label={text || "Loading"}
		>
			<span className="sr-only">{text || "Loading..."}</span>{" "}
			{/* Accessibility */}
		</div>
	);

	// Content (Spinner + Optional Text)
	const content = (
		<>
			{spinnerElement}
			{text && (
				<p
					className={`text-white ${
						size === "sm" || variant === "button" ? "text-xs" : "text-sm" // Smaller text for smaller spinners
					} ${variant === "full-page" ? "mt-3 text-base" : ""} ${
						variant === "overlay" ? "mt-2" : ""
					}`}
				>
					{text}
				</p>
			)}
		</>
	);

	// --- Render based on variant ---

	// Variant for use inside buttons (usually just the spinner)
	if (variant === "button") {
		return (
			<div className={`inline-flex items-center justify-center ${className}`}>
				{spinnerElement}
				{/* Button text is usually handled by the button itself */}
			</div>
		);
	}

	// Variant for inline display (flows with text)
	if (variant === "inline") {
		return (
			<div className={`inline-flex items-center gap-2 ${className}`}>
				{content}
			</div>
		);
	}

	// Variant for overlaying a specific parent container
	// IMPORTANT: Parent element needs `position: relative` in its CSS/Tailwind classes.
	if (variant === "overlay") {
		return (
			<div
				className={`absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 ${overlayColor} ${className}`}
			>
				{content}
			</div>
		);
	}

	// Variant for full-page overlay (like the original component)
	if (variant === "full-page") {
		// Use size 'xl' by default for full page unless overridden
		const currentSize = size === "md" ? "xl" : size; // Upgrade default md to xl for full-page
		const fullPageSpinnerElement = (
			<div
				className={`animate-spin rounded-full border-primary border-t-transparent ${
					sizeClasses[currentSize] || sizeClasses["xl"]
				}`}
				role="status"
				aria-live="polite"
				aria-label={text || "Loading"}
			>
				<span className="sr-only">{text || "Loading..."}</span>
			</div>
		);
		const fullPageContent = (
			<>
				{fullPageSpinnerElement}
				{text && <p className="text-gray-600 text-base mt-3">{text}</p>}
			</>
		);

		return (
			<div
				className={`fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 ${overlayColor} ${className}`}
			>
				{fullPageContent}
			</div>
		);
	}

	// Fallback to inline if variant is unrecognized
	return (
		<div className={`inline-flex items-center gap-2 ${className}`}>
			{content}
		</div>
	);
};
